<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <div class="border-b bg-white">
      <div class="max-w-4xl mx-auto px-6 py-4">
        <NuxtLink to="/" class="text-blue-600 hover:text-blue-700 font-medium">
          ← Back to Home
        </NuxtLink>
      </div>
    </div>

    <!-- Content -->
    <main class="max-w-4xl mx-auto px-6 py-12">
      <h1 class="text-3xl md:text-4xl font-extrabold text-slate-900 mb-8">
        Privacy Policy
      </h1>

      <div class="prose prose-slate max-w-none">
        <p class="text-lg text-slate-600 mb-8">
          GVGmall respects your privacy. This policy explains how we collect, use, and protect your personal information when you visit our website or contact us for support.
        </p>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Information We Collect</h2>
          <p class="text-slate-600 mb-4">
            We collect only the information necessary to provide our services and support:
          </p>
          <ul class="list-disc pl-6 text-slate-600 space-y-2">
            <li>Contact information (name, email address) when you reach out to our support team</li>
            <li>Order information necessary for processing and delivery of digital licenses</li>
            <li>Technical information required to troubleshoot and resolve support requests</li>
          </ul>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">How We Use Your Information</h2>
          <p class="text-slate-600 mb-4">
            Your information is used exclusively for:
          </p>
          <ul class="list-disc pl-6 text-slate-600 space-y-2">
            <li>Responding to your support inquiries and questions</li>
            <li>Processing and delivering your digital software licenses</li>
            <li>Improving our customer service and support quality</li>
            <li>Complying with legal obligations when required</li>
          </ul>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Data Protection</h2>
          <p class="text-slate-600">
            We implement appropriate security measures to protect your personal information. We do not sell, trade, or rent your personal data to third parties. Your information is shared only with trusted service providers necessary to fulfill our services (such as payment processors and email delivery services), and only under strict confidentiality agreements.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Cookies and Analytics</h2>
          <p class="text-slate-600">
            We may use cookies and similar technologies to improve website functionality and user experience. These technologies do not collect personally identifiable information beyond what you voluntarily provide.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Your Rights</h2>
          <p class="text-slate-600">
            You have the right to access, correct, or request deletion of your personal information. To exercise these rights or for any privacy-related questions, please contact us.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Contact Us</h2>
          <p class="text-slate-600 mb-4">
            For any privacy-related inquiries or requests:
          </p>
          <p class="text-slate-900">
            <strong>Email:</strong> <a href="mailto:support@gvgmall.co" class="text-blue-600 hover:text-blue-700">support@gvgmall.co</a>
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Policy Updates</h2>
          <p class="text-slate-600">
            This privacy policy may be updated periodically to reflect changes in our practices or legal requirements. We encourage you to review this page regularly for the latest information.
          </p>
        </section>

        <p class="text-sm text-slate-500 mt-12">
          Last updated: June 2025
        </p>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'institutional'
})

useSeoMeta({
  title: 'Privacy Policy | GVGmall',
  description: 'GVGmall privacy policy - Learn how we protect your personal information and respect your privacy.',
  ogTitle: 'Privacy Policy | GVGmall',
  ogDescription: 'GVGmall privacy policy - Learn how we protect your personal information.',
  robots: 'index,follow'
})

useHead({
  link: [
    { rel: 'canonical', href: 'https://www.gvgmall.co/legal/privacy-policy' }
  ]
})
</script>
